'use client';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { nearestInteger } from '../../../../../apps/frontend/src/utils/utilFunctions';
import TableHeader from '../../components/generic/TableHeader';
import SubjectTabs from '../generic/SubjectTabs';
import NoData from '../generic/NoData';
import { Checkbox } from '../../components/ui/checkbox';

const CustomTooltip = ({ active, payload, label, isDark, onResetFilters }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className={`p-3 rounded-lg shadow-md ${
          isDark ? 'bg-[var(--q3-surface-dark)]' : 'bg-[var(--q3-surface-default)]'
        }`}
      >
        <p className="font-bold mb-2">{label}</p>
        <div className="flex flex-col gap-1">
          {payload.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
              <span className="text-sm">
                {item.name}: {item.value}%
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const TimelineChart = ({ data, onResetFilters }) => {
  const [activeSubject, setActiveSubject] = useState('overall');
  const [activeLines, setActiveLines] = useState({
    score: true,
    attempted: true,
    accuracy: true,
  });
  const [subjects, setSubjects] = useState([]);
  const { isDark } = useSelector((state) => state.user);

  // ✅ Extract all unique subjects from ALL tests (not just first one)
  useEffect(() => {
    if (data?.length > 0) {
      const allSubjects = new Map();

      data.forEach(({ basicAnalysis }) => {
        if (basicAnalysis?.subjectWise) {
          basicAnalysis.subjectWise.forEach((subject) => {
            if (subject?.subjectMeta?._id && !allSubjects.has(subject.subjectMeta._id)) {
              allSubjects.set(subject.subjectMeta._id, {
                _id: subject.subjectMeta._id,
                title: subject.subjectMeta.title,
                icon: subject.subjectMeta.icon,
                darkIcon: subject.subjectMeta.darkIcon,
                color: subject.subjectMeta.color,
              });
            }
          });
        }
      });

      setSubjects(Array.from(allSubjects.values()));
    }
  }, [data]);

  const toggleLine = (line) => {
    setActiveLines((prev) => ({
      ...prev,
      [line]: !prev[line],
    }));
  };

  // ✅ Map test-wise data for chart
  const chartData =
    data
      ?.map((item) => {
        let subjectData;

        if (activeSubject === 'overall') {
          subjectData = item.basicAnalysis?.subjectWise?.find((s) => s.subjectMeta?._id === 'overall');
        } else {
          subjectData = item.basicAnalysis?.subjectWise?.find((subject) => subject?.subjectMeta?._id === activeSubject);
        }

        if (!subjectData) return null;

        return {
          name: item.basicAnalysis.testTitle,
          score: nearestInteger((subjectData.marksScored / subjectData.totalMarks) * 100),
          attempted: nearestInteger((subjectData.questionAttempted / subjectData.totalQuestions) * 100),
          accuracy: nearestInteger(subjectData.accuracy),
          date: new Date(item.basicAnalysis.submittedAt),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.date - b.date) || [];

  const formatXAxisTick = (value) => {
    if (value.length > 15) {
      return `${value.substring(0, 12)}...`;
    }
    return value;
  };

  if (!data || data.length === 0) {
    return (
      <div className="text-center p-8 border border-[var(--q3-stroke-light)] rounded-lg">
        <NoData onResetAll={onResetFilters} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <TableHeader headerTitle="Test-wise Breakdown - Graphical view" showBottomBorder={false} />

      {/* Subject Tabs - outside scroll container */}
      <div className="border-r border-l border-[var(--q3-stroke-light)] bg-[var(--q3-surface-default)]">
        <SubjectTabs activeSubject={activeSubject} onSubjectChange={setActiveSubject} subjects={subjects} />
      </div>

      {/* Scrollable Graph + Controls */}
      <div className="overflow-x-auto">
        <div className="min-w-[600px] border-x border-b border-[var(--q3-stroke-light)] rounded-b-lg overflow-hidden">
          <div className="border-b border-[var(--q3-stroke-light)] p-6">
            {/* Checkbox controls */}
            <div className="overflow-x-auto pb-2">
              <div className="flex items-center gap-6 mb-6 min-w-max">
                {/* Score */}
                <label className="flex flex-col items-center cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={activeLines.score}
                      onCheckedChange={() => toggleLine('score')}
                      className="data-[state=checked]:bg-[#ff7000] data-[state=checked]:border-[#ff7000] w-4 h-4 cursor-pointer"
                    />
                    <span className="text-sm text-[var(--q3-neutral-default)]">Score</span>
                  </div>
                  <div
                    className="h-1 w-full mt-1 rounded-full"
                    style={{
                      backgroundColor: activeLines.score ? 'var(--q3-orange-normal)' : 'transparent',
                    }}
                  />
                </label>

                {/* Qs Attempted */}
                <label className="flex flex-col items-center cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={activeLines.attempted}
                      onCheckedChange={() => toggleLine('attempted')}
                      className="data-[state=checked]:bg-[#37b24d] data-[state=checked]:border-[#37b24d] w-4 h-4"
                    />
                    <span className="text-sm text-[var(--q3-neutral-default)]">Qs Attempted</span>
                  </div>
                  <div
                    className="h-1 w-full mt-1 rounded-full"
                    style={{
                      backgroundColor: activeLines.attempted ? 'var(--q3-green-normal)' : 'transparent',
                    }}
                  />
                </label>

                {/* Accuracy */}
                <label className="flex flex-col items-center cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={activeLines.accuracy}
                      onCheckedChange={() => toggleLine('accuracy')}
                      className="data-[state=checked]:bg-[#ff1a00] data-[state=checked]:border-[#ff1a00] w-4 h-4"
                    />
                    <span className="text-sm text-[var(--q3-neutral-default)]">Accuracy</span>
                  </div>
                  <div
                    className="h-1 w-full mt-1 rounded-full"
                    style={{
                      backgroundColor: activeLines.accuracy ? '#ff1a00' : 'transparent',
                    }}
                  />
                </label>
              </div>
            </div>

            {/* Chart container */}
            <div className="h-80 w-full min-w-[600px] select-none [&_*]:select-none">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 20, right: 20, bottom: 40, left: 0 }}>
                  <CartesianGrid
                    stroke={isDark ? 'var(--q3-stroke-dark)' : 'var(--q3-stroke-light)'}
                    strokeWidth={0.5}
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fontSize: 12,
                      fill: 'var(--q3-neutral-light)',
                      angle: -35,
                      textAnchor: 'end',
                    }}
                    tickFormatter={formatXAxisTick}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }}
                    domain={[0, 100]}
                    ticks={[0, 20, 40, 60, 80, 100]}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />

                  {activeLines.score && (
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#ff7000"
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                      name="Score"
                    />
                  )}
                  {activeLines.attempted && (
                    <Line
                      type="monotone"
                      dataKey="attempted"
                      stroke="#37b24d"
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                      name="Qs Attempted"
                    />
                  )}
                  {activeLines.accuracy && (
                    <Line
                      type="monotone"
                      dataKey="accuracy"
                      stroke="#ff1a00"
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                      name="Accuracy"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineChart;
